/**
 * @file index.tsx
 *
 * @fileoverview Renders a CTA product box from a CTAProductParagraph type.
 */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Link from '~/jobiqo/components/Link';
import Price from '~/jobiqo/models/price';
import { CtaProductParagraph, TextAlignment } from '~/jobiqo/types/types';
import { getFormattedPageLink } from '~/jobiqo/utils/routes';
import { LinkButton } from '~/vcl/atoms/01-forms/01-button/02-link-button';
import { WYSIWYGText } from '~/vcl/atoms/13-text/02-WYSIWYG';
import { ProductBox } from '~/vcl/organisms/10-commerce/01-product-box';
import styles from './CTAProductBox.module.scss';
import Image from '~/jobiqo/components/Image';
import NextLink from 'next/link';

interface CTAProductBoxProps {
  /**
   * The product to display.
   */
  product: CtaProductParagraph;
}

function getAlignmentValues(alignment: TextAlignment) {
  switch (alignment) {
    case 'LEFT':
      return 'left';
    case 'RIGHT':
      return 'right';
    case 'CENTER':
      return 'center';
    default:
      return 'left';
  }
}

export default function CTAProductBox({ product }: CTAProductBoxProps) {
  const productPrice =
    product?.priceNumber &&
    product?.priceCurrency &&
    new Price(product?.priceNumber, product?.priceCurrency);

  const buttonText = !!product.button?.text ? (
    <>{product.button?.text}</>
  ) : (
    <FormattedMessage id="product_grid_item_buy" defaultMessage="Buy now" />
  );

  return (
    <ProductBox
      title={product.title}
      intro={product.intro}
      {...(product.image
        ? {
            banner: (
              <Image
                src={product.image.url}
                alt={product.image.alt ?? product.title}
                title={product.image.title ?? product.title}
                width={product.image.width ?? 1000}
                height={product.image.height ?? 500}
              />
            ),
          }
        : null)}
      {...(productPrice
        ? { price: productPrice.getPriceRepresentation() }
        : null)}
      {...(product.titleIntroAlignment
        ? { textAlignment: getAlignmentValues(product.titleIntroAlignment) }
        : null)}
      {...(product.button
        ? {
            button: (
              <>
                {product.button.url ? (
                  <Link prefetch={false} asChild>
                    <LinkButton
                      buttonStyle="primary"
                      linkElement={NextLink}
                      {...getFormattedPageLink(product.button?.url)}
                      className={styles.button}
                    >
                      {buttonText}
                    </LinkButton>
                  </Link>
                ) : (
                  <Link href="/contact" prefetch={false} asChild>
                    <LinkButton linkElement={NextLink} buttonStyle="primary">
                      {buttonText}
                    </LinkButton>
                  </Link>
                )}
              </>
            ),
          }
        : null)}
      {...(product.buttonAlignment
        ? { buttonAlignment: getAlignmentValues(product.buttonAlignment) }
        : null)}
      {...(product.content
        ? {
            content: (
              <WYSIWYGText format="filtered_html" content={product.content} />
            ),
          }
        : null)}
    />
  );
}
